import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type Assurance={
  counts:{
    submittedEvidence:number;
    draftEvaluations:number;
    candidateAchievements:number;
    credentialRequests:number;
  };
  items:Array<{
    kind:string;
    resourceId:string;
    displayName:string;
    title:string;
    status:string;
    occurredAt:string;
    actionLabel:string;
    blocked?:boolean;
  }>;
};

type Report={
  summary:{
    programCount:number;
    activeProgramCount:number;
    learnerReach:number;
    completedParticipationCount:number;
    verifiedEvidenceCount:number;
  };
};

type Learner={
  learner_id:string;
  display_name:string;
  grade_level?:string|null;
  class_label?:string|null;
  participation_count:number;
  evidence_count:number;
  credential_count:number;
  last_activity_at?:string|null;
};

type AssessmentWorkspace={
  assessments:Array<{
    versionId:string;
    instrumentName:string;
    title:string;
    estimatedMinutes?:number|null;
    assignedCount:number;
    completedCount:number;
  }>;
};

type Badge={
  badgeKey:string;
  teacherName:string;
  evidenceReadyCount:number;
  issuedCount:number;
};

type ClassWorkspace={
  classes:Array<{classGroupId:string}>;
  students:Array<{nextFollowUpAt?:string|null}>;
};

type TaskSummary={
  counts:{total:number;high:number};
};

type CaseloadSummary={
  mySummary:{caseCount:number;highUrgentCount:number;incomingHandoverCount:number};
};

type PrivacySummary={
  counts:{learnersWithoutGuardian:number;pendingConsent:number;activeConsent:number;activeShares:number};
};

type PartnerSummary={
  counts:{partnerCount:number;activePartnerCount:number;pendingSessionCount:number;confirmedPlacementCount:number;followUpDueCount:number};
};

type Lifecycle={
  counts:{
    activeStudents:number;
    plannedStudents:number;
    outgoingTransfers:number;
    incomingTransfers:number;
    preparedTransitions:number;
  };
};

function navigate(path:string){
  window.history.pushState({},"",path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function pathFor(kind:string){
  if(kind==="EVIDENCE_REVIEW") return "/evidence";
  if(kind==="EVALUATION_CONFIRM" || kind==="ACHIEVEMENT_CONFIRM") return "/evaluation";
  return "/assurance";
}

function learnerPath(id:string){
  return `/learners?student=${encodeURIComponent(id)}`;
}

export function CenterTodayPage({
  client,
  schoolId
}:{
  client:PlatformClient;
  schoolId:string;
}){
  const [assurance,setAssurance]=useState<Assurance|null>(null);
  const [report,setReport]=useState<Report|null>(null);
  const [learners,setLearners]=useState<Learner[]>([]);
  const [assessmentWorkspace,setAssessmentWorkspace]=useState<AssessmentWorkspace|null>(null);
  const [badges,setBadges]=useState<Badge[]>([]);
  const [lifecycle,setLifecycle]=useState<Lifecycle|null>(null);
  const [classWorkspace,setClassWorkspace]=useState<ClassWorkspace|null>(null);
  const [taskCenter,setTaskCenter]=useState<TaskSummary|null>(null);
  const [caseload,setCaseload]=useState<CaseloadSummary|null>(null);
  const [privacy,setPrivacy]=useState<PrivacySummary|null>(null);
  const [partners,setPartners]=useState<PartnerSummary|null>(null);
  const [query,setQuery]=useState("");
  const [error,setError]=useState("");

  useEffect(()=>{
    let active=true;
    setError("");

    Promise.all([
      client.rpc("center_assurance_workbench",{p_school_id:schoolId}),
      client.rpc("center_program_report",{p_school_id:schoolId}),
      client.rpc("center_learners",{p_school_id:schoolId}),
      client.rpc("center_assessment_workspace",{p_school_id:schoolId}),
      client.rpc("center_badge_catalog",{p_school_id:schoolId}),
      client.rpc("center_student_lifecycle_workspace",{p_school_id:schoolId}),
      client.rpc("center_class_counseling_workspace",{p_school_id:schoolId,p_academic_year_id:null}),
      client.rpc("center_task_center_v2",{p_school_id:schoolId}),
      client.rpc("center_case_caseload_workspace",{p_school_id:schoolId}),
      client.rpc("center_privacy_workspace",{p_school_id:schoolId,p_learner_id:null}),
      client.rpc("center_partner_workspace",{p_school_id:schoolId,p_relationship_id:null})
    ]).then(([a,r,l,assessment,badge,lifecycleResult,classResult,taskResult,caseloadResult,privacyResult,partnerResult])=>{
      if(!active)return;
      const firstError=a.error || r.error || l.error || assessment.error || badge.error || lifecycleResult.error || classResult.error || taskResult.error || caseloadResult.error || privacyResult.error || partnerResult.error;
      if(firstError){setError(firstError.message);return;}
      setAssurance(a.data as Assurance);
      setReport(r.data as Report);
      setLearners((l.data || []) as Learner[]);
      setAssessmentWorkspace(assessment.data as AssessmentWorkspace);
      setBadges((badge.data || []) as Badge[]);
      setLifecycle(lifecycleResult.data as Lifecycle);
      setClassWorkspace(classResult.data as ClassWorkspace);
      setTaskCenter(taskResult.data as TaskSummary);
      setCaseload(caseloadResult.data as CaseloadSummary);
      setPrivacy(privacyResult.data as PrivacySummary);
      setPartners(partnerResult.data as PartnerSummary);
    });

    return()=>{active=false;};
  },[client,schoolId]);

  const searchedLearners=useMemo(()=>{
    const value=query.trim().toLowerCase();
    if(!value)return learners.slice(0,6);
    return learners.filter((item)=>{
      const haystack=[
        item.display_name,
        item.grade_level ? `${item.grade_level}학년` : "",
        item.class_label ? `${item.class_label}반` : ""
      ].join(" ").toLowerCase();
      return haystack.includes(value);
    }).slice(0,8);
  },[learners,query]);

  if(error) return <p className="error">{error}</p>;
  if(!assurance || !report || !assessmentWorkspace || !lifecycle || !classWorkspace || !taskCenter || !caseload || !privacy || !partners) return <p className="muted">교사 홈을 준비하는 중…</p>;

  const reviewTotal=
    assurance.counts.submittedEvidence+
    assurance.counts.draftEvaluations+
    assurance.counts.candidateAchievements+
    assurance.counts.credentialRequests;

  const readyBadgeCount=badges.reduce((sum,item)=>sum+Number(item.evidenceReadyCount || 0),0);

  return (
    <section className="teacher-home">
      <header className="teacher-home-hero">
        <div>
          <p className="eyebrow">교사 홈</p>
          <h2>오늘 어떤 학생을 도울까요?</h2>
          <p>학생을 찾거나, 검사·활동·배지 업무를 바로 시작하세요.</p>
        </div>
        <div className="teacher-review-chip">
          <span>미처리 업무</span>
          <strong>{taskCenter.counts.total}</strong>
          <small>우선 {taskCenter.counts.high}</small>
        </div>
      </header>

      <section className="teacher-student-finder">
        <div className="teacher-finder-copy">
          <strong>학생 바로 찾기</strong>
          <span>이름·학년·반으로 찾으면 진로기록으로 바로 이동합니다.</span>
        </div>
        <input
          className="teacher-search"
          value={query}
          onChange={(event)=>setQuery(event.target.value)}
          placeholder="예: 김민지, 2학년, 3반"
          aria-label="학생 검색"
        />
        <div className="teacher-search-results">
          {searchedLearners.map((learner)=>(
            <button key={learner.learner_id} onClick={()=>navigate(learnerPath(learner.learner_id))}>
              <span>
                <strong>{learner.display_name}</strong>
                <small>
                  {learner.grade_level ? `${learner.grade_level}학년` : "학년 미정"}
                  {learner.class_label ? ` · ${learner.class_label}반` : ""}
                </small>
              </span>
              <b>열기</b>
            </button>
          ))}
          {!searchedLearners.length && <p>검색되는 학생이 없습니다.</p>}
        </div>
      </section>

      <div className="teacher-primary-actions">
        <button onClick={()=>navigate("/assurance")}><span className="teacher-action-icon">※</span><div><strong>업무안심</strong><small>정책·학교확인·진로업무 연결</small></div></button>
        <button className={caseload.mySummary.highUrgentCount>0 || caseload.mySummary.incomingHandoverCount>0 ? "ready" : ""} onClick={()=>navigate("/caseload")}><span className="teacher-action-icon">◉</span><div><strong>Case 관리</strong><small>{caseload.mySummary.caseCount}건 · 우선·긴급 {caseload.mySummary.highUrgentCount} · 인수 {caseload.mySummary.incomingHandoverCount}</small></div></button>
        <button className={privacy.counts.pendingConsent>0 || privacy.counts.learnersWithoutGuardian>0 ? "ready" : ""} onClick={()=>navigate("/privacy")}><span className="teacher-action-icon">◌</span><div><strong>동의·공유</strong><small>동의대기 {privacy.counts.pendingConsent} · 보호자 미등록 {privacy.counts.learnersWithoutGuardian} · 공유 {privacy.counts.activeShares}</small></div></button>
        <button className={taskCenter.counts.high>0 ? "ready" : ""} onClick={()=>navigate("/operations")}>
          <span className="teacher-action-icon">☑</span>
          <div><strong>업무함</strong><small>{taskCenter.counts.total}건 미처리 · 우선 {taskCenter.counts.high}건</small></div>
        </button>
        <button onClick={()=>navigate("/learners")}>
          <span className="teacher-action-icon">◎</span>
          <div><strong>학생 보기</strong><small>{learners.length}명 · 진로기록과 다음 할 일</small></div>
        </button>
        <button onClick={()=>navigate("/student-lifecycle")}>
          <span className="teacher-action-icon">↕</span>
          <div><strong>학적관리</strong><small>전입·전출 ${lifecycle.counts.incomingTransfers+lifecycle.counts.outgoingTransfers}건 · 학년이동 ${lifecycle.counts.preparedTransitions}건</small></div>
        </button>
        <button onClick={()=>navigate("/classes")}>
          <span className="teacher-action-icon">▦</span>
          <div><strong>학급·상담</strong><small>{classWorkspace.classes.length}개 학급 · 후속상담 {classWorkspace.students.filter((item)=>item.nextFollowUpAt).length}명</small></div>
        </button>
        <button onClick={()=>navigate("/assessments")}>
          <span className="teacher-action-icon">◇</span>
          <div><strong>진로검사 보내기</strong><small>{assessmentWorkspace.assessments.length}개 검사 · 학년·반 일괄 배정</small></div>
        </button>
        <button className={partners.counts.pendingSessionCount>0 || partners.counts.followUpDueCount>0 ? "ready" : ""} onClick={()=>navigate("/partners")}><span className="teacher-action-icon">✥</span><div><strong>체험처·파트너</strong><small>승인 {partners.counts.activePartnerCount}곳 · 확약대기 {partners.counts.pendingSessionCount} · 배치 {partners.counts.confirmedPlacementCount}</small></div></button>
        <button onClick={()=>navigate("/programs")}>
          <span className="teacher-action-icon">□</span>
          <div><strong>수업·활동 운영</strong><small>{report.summary.activeProgramCount}개 운영 중</small></div>
        </button>
        <button className={readyBadgeCount>0 ? "ready" : ""} onClick={()=>navigate("/badges")}>
          <span className="teacher-action-icon">✦</span>
          <div><strong>디지털배지</strong><small>{readyBadgeCount}명 발급 준비</small></div>
        </button>
      </div>

      <div className="teacher-home-columns">
        <section className="teacher-home-panel">
          <div className="panel-title">
            <div>
              <strong>지금 확인하면 좋은 일</strong>
              <span>미처리 업무를 학생 중심으로 모았습니다.</span>
            </div>
            <button className="text-button" onClick={()=>navigate("/evidence")}>검토함 열기</button>
          </div>
          <div className="teacher-review-list">
            {assurance.items.slice(0,6).map((item)=>(
              <button key={item.kind+"-"+item.resourceId} onClick={()=>navigate(pathFor(item.kind))}>
                <span>
                  <strong>{item.displayName}</strong>
                  <small>{item.title}</small>
                </span>
                <b>{item.actionLabel}</b>
              </button>
            ))}
            {!assurance.items.length && (
              <div className="teacher-clear-state">
                <strong>지금은 확인할 일이 없습니다.</strong>
                <span>학생 상담이나 다음 수업 준비를 시작해도 좋습니다.</span>
              </div>
            )}
          </div>
        </section>

        <section className="teacher-home-panel">
          <div className="panel-title">
            <div>
              <strong>이번 운영 현황</strong>
              <span>교사가 자주 보는 숫자만 간단히 표시합니다.</span>
            </div>
            <button className="text-button" onClick={()=>navigate("/reports")}>리포트</button>
          </div>
          <div className="teacher-operation-grid">
            <Metric label="도달 학생" value={report.summary.learnerReach} />
            <Metric label="활동 완료" value={report.summary.completedParticipationCount} />
            <Metric label="검증된 결과물" value={report.summary.verifiedEvidenceCount} />
            <Metric label="발급 준비" value={readyBadgeCount} />
          </div>
        </section>
      </div>
    </section>
  );
}

function Metric({label,value}:{label:string;value:number}){
  return <div className="teacher-metric"><span>{label}</span><strong>{value}</strong></div>;
}
